import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronsUpDown, Plus, Trash2, Edit2, Loader2, FileUp } from "lucide-react";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import { defaultTemplate, type Template } from "../../../../../../sdkStructs";
import { apiTemplateCreate, apiTemplateRetrieve, apiTemplateUpdate } from "../../../../../../sdk";
import styles from "./templateChooser.module.css";
import { AppContext } from "../../../../../../contexts/AppContext";
import { omit } from "lodash";
import { AlertType } from "../../../../../Common/AlertManager/AlertManager";
import { handleUploadDocxTemplate, UploadDocxProps } from "../../docxFunctions";

interface TemplateSelectorProps {
  onTemplateSelect: (templateId: number) => void;
  hasUnsavedChanges: boolean;
  onSave: () => void;
  onDelete: (id: number) => Promise<void>;
  isTemplateLoading?: boolean;
}

// Local storage key for the last selected template
const LAST_SELECTED_TEMPLATE_KEY = "lastSelectedTemplateId";

export default function TemplateChooser({
  onTemplateSelect,
  hasUnsavedChanges,
  onSave,
  onDelete,
  isTemplateLoading = false,
}: Readonly<TemplateSelectorProps>) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useContext(AuthContext);
  const { setShowModal, pushAlert } = useContext(AppContext);

  const uploadDocxProps: UploadDocxProps = useMemo(() => {return ({
    token,
    setShowModal,
    isTemplateLoading,
    stateProps: {
      setLoadingFunc: setIsLoading,
      selectTemplateFunc: (template: Template) => {
        onTemplateSelect(template.ID);
        setSelectedTemplate(template);
      },
      pushAlert,
    },
  })}, [isTemplateLoading, onTemplateSelect, pushAlert, setShowModal, token]);

  // Helper function to display alerts (replacing showToast)
  const showAlert = useCallback(
    (type: AlertType, message: string) => {
      pushAlert({
        title: type === "error" ? "Error" : "Success",
        text: message,
        type: type,
        closeButton: true,
        time: 5000,
      });
    },
    [pushAlert],
  );

  // Load templates and restore last selected template
  const loadTemplates = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiTemplateRetrieve(token);

      if (response.data) {
        setTemplates(response.data);

        // Try to restore the last selected template
        const lastSelectedId = localStorage.getItem(LAST_SELECTED_TEMPLATE_KEY);
        if (lastSelectedId) {
          const lastTemplate = response.data.find((template) => template.ID === parseInt(lastSelectedId));

          if (lastTemplate) {
            setSelectedTemplate(lastTemplate);
            onTemplateSelect(lastTemplate.ID);
          } else if (response.data.length > 0) {
            // If the previously selected template is gone, select the first available one
            setSelectedTemplate(response.data[0]);
            onTemplateSelect(response.data[0].ID);
            localStorage.setItem(LAST_SELECTED_TEMPLATE_KEY, String(response.data[0].ID));
          } else {
            // No templates available
            setSelectedTemplate(null);
            localStorage.removeItem(LAST_SELECTED_TEMPLATE_KEY);
          }
        } else if (response.data.length > 0) {
          // No last selected template, select the first one
          setSelectedTemplate(response.data[0]);
          onTemplateSelect(response.data[0].ID);
          localStorage.setItem(LAST_SELECTED_TEMPLATE_KEY, String(response.data[0].ID));
        }
      }
    } catch (err) {
      setError("Failed to load templates");
      showAlert("error", "Failed to load templates");
      console.error("Failed to load templates:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token, onTemplateSelect, showAlert]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Save last selected template ID to local storage
  useEffect(() => {
    if (selectedTemplate) {
      localStorage.setItem(LAST_SELECTED_TEMPLATE_KEY, String(selectedTemplate.ID));
    }
  }, [selectedTemplate]);

  const handleCreateTemplate = useCallback(
    async (name: string) => {
      try {
        setIsLoading(true);
        const requestTemplate: Template = { ...defaultTemplate, TemplateName: name };
        const templateWithoutId = omit(requestTemplate, "ID");
        const response = await apiTemplateCreate(token, templateWithoutId);
        const newTemplate = response.data;

        setTemplates((prev) => [...prev, newTemplate]);
        setSelectedTemplate(newTemplate);
        onTemplateSelect(newTemplate.ID);
        setIsOpen(false);
        showAlert("success", "Template created successfully");
      } catch (error) {
        console.error("Failed to create template:", error);
        showAlert("error", "Failed to create template");
      } finally {
        setIsLoading(false);
      }
    },
    [token, onTemplateSelect, showAlert],
  );

  const handleUpdateTemplate = useCallback(
    async (name: string) => {
      if (!selectedTemplate) return;
      try {
        setIsLoading(true);
        const updatedTemplate = { ...selectedTemplate, TemplateName: name };
        const response = await apiTemplateUpdate(token, updatedTemplate.ID, updatedTemplate);
        const updatedTemplates = templates.map((template) =>
          template.ID === updatedTemplate.ID ? response.data.template : template,
        );
        setTemplates(updatedTemplates);
        setSelectedTemplate(response.data.template);
        onTemplateSelect(response.data.template.ID);
        showAlert("success", "Template updated successfully");
      } catch (error) {
        console.error("Failed to update template:", error);
        showAlert("error", "Failed to update template");
      } finally {
        setIsLoading(false);
      }
    },
    [selectedTemplate, templates, token, showAlert, onTemplateSelect],
  );

  const handleDeleteTemplate = useCallback(
    async (templateId: number) => {
      try {
        setIsLoading(true);
        // First call the parent's delete function
        await onDelete(templateId);

        // Get updated templates list after deletion
        const updatedTemplates = templates.filter((template) => template.ID !== templateId);
        setTemplates(updatedTemplates);

        if (selectedTemplate?.ID === templateId) {
          // If we deleted the currently selected template
          if (updatedTemplates.length > 0) {
            // Select the first available template
            const firstTemplate = updatedTemplates[0];
            setSelectedTemplate(firstTemplate);
            onTemplateSelect(firstTemplate.ID);
            localStorage.setItem(LAST_SELECTED_TEMPLATE_KEY, String(firstTemplate.ID));
          } else {
            // No templates left
            setSelectedTemplate(null);
            localStorage.removeItem(LAST_SELECTED_TEMPLATE_KEY);
          }
        }
        showAlert("success", "Template deleted successfully");
      } catch (error) {
        console.error("Failed to delete template:", error);
        showAlert("error", "Failed to delete template");
      } finally {
        setIsLoading(false);
      }
    },
    [onDelete, templates, selectedTemplate, onTemplateSelect, showAlert],
  );

  const getNewTemplateName = useCallback(() => {
    if (isTemplateLoading) return;

    setShowModal("name", {
      title: "Template Name",
      submit: handleCreateTemplate,
      close: () => setShowModal("none", undefined),
    });
  }, [setShowModal, handleCreateTemplate, isTemplateLoading]);

  const getUpdateTemplateName = useCallback(() => {
    if (!selectedTemplate || isTemplateLoading) return;

    setShowModal("name", {
      title: "Update Template Name",
      submit: handleUpdateTemplate,
      close: () => setShowModal("none", undefined),
      initialValue: selectedTemplate.TemplateName,
    });
  }, [setShowModal, handleUpdateTemplate, selectedTemplate, isTemplateLoading]);

  const confirmDeleteTemplate = useCallback(
    (templateId: number) => {
      if (isTemplateLoading) return;

      setShowModal("confirm", {
        text: "Are you sure you want to delete this template?",
        confirm: () => handleDeleteTemplate(templateId),
        close: () => setShowModal("none", undefined),
      });
    },
    [setShowModal, handleDeleteTemplate, isTemplateLoading],
  );

  const selectTemplate = useCallback(
    (template: Template) => {
      if (isTemplateLoading) return;

      setSelectedTemplate(template);
      onTemplateSelect(template.ID);
      setIsOpen(false);
    },
    [onTemplateSelect, isTemplateLoading],
  );

  // Memoize the template list rendering for better performance
  const renderTemplateList = useMemo(() => {
    if (isLoading && templates.length === 0) {
      return (
        <div className={styles.loadingState}>
          <Loader2 size={24} className={styles.spinnerIcon} />
          <span>Loading templates...</span>
        </div>
      );
    }

    if (error && templates.length === 0) {
      return (
        <div className={styles.errorState}>
          <span>Failed to load templates. Try again.</span>
          <button className={styles.retryButton} onClick={loadTemplates}>
            Retry
          </button>
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className={styles.emptyState}>
          <span className={styles.noTemplates}>No templates available.</span>
          <div className={styles.templateActions}>
            <button
              className={styles.templateActionButton}
              onClick={getNewTemplateName}
              aria-label="Create new template"
            >
              <Plus size={16} className={styles.icon} />
              Create Template
            </button>
            <button
              className={styles.templateActionButton}
              onClick={() => handleUploadDocxTemplate(uploadDocxProps)}
              aria-label="Upload DOCX template"
            >
              <FileUp size={16} className={styles.icon} />
              Upload Template
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={styles.templateList} role="listbox" aria-label="Available templates">
          {templates.map((template) => (
            <div key={template.ID} className={styles.templateItem}>
              <option
                className={`${styles.templateButton} ${
                  selectedTemplate?.ID === template.ID ? styles.selectedTemplate : ""
                } ${template.DocxPath ? styles.docxTemplate : ""}`}
                onClick={() => selectTemplate(template)}
                aria-selected={selectedTemplate?.ID === template.ID}
                disabled={isTemplateLoading}
              >
                {template.TemplateName ? template.TemplateName : "Template " + template.ID}
                {template.DocxPath && " 📄"}
              </option>
              <div className={styles.actionButtons}>
                {selectedTemplate?.ID === template.ID && (
                  <button
                    className={styles.editButton}
                    onClick={getUpdateTemplateName}
                    title="Rename template"
                    aria-label={`Rename template ${template.TemplateName || template.ID}`}
                    disabled={isTemplateLoading}
                  >
                    <Edit2 size={16} color="white" />
                  </button>
                )}
                <button
                  className={styles.deleteButton}
                  onClick={() => confirmDeleteTemplate(template.ID)}
                  title="Delete template"
                  aria-label={`Delete template ${template.TemplateName || template.ID}`}
                  disabled={isTemplateLoading}
                >
                  <Trash2 size={16} color="white" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.templateActions}>
          <button
            className={styles.templateActionButton}
            onClick={getNewTemplateName}
            aria-label="Create new template"
            disabled={isTemplateLoading}
          >
            <Plus size={16} className={styles.icon} />
            Pdf Template
          </button>
          <button
            className={styles.templateActionButton}
            onClick={() => handleUploadDocxTemplate(uploadDocxProps)}
            aria-label="Upload DOCX template"
            disabled={isTemplateLoading}
          >
            <FileUp size={16} className={styles.icon} />
            Docx Template
          </button>
        </div>
      </>
    );
  }, [
    templates,
    selectedTemplate,
    isLoading,
    error,
    loadTemplates,
    selectTemplate,
    confirmDeleteTemplate,
    getNewTemplateName,
    getUpdateTemplateName,
    isTemplateLoading,
    uploadDocxProps,
  ]);

  function getUITemplateName(){
    if(!selectedTemplate)
      return "Select template...";
    if(!selectedTemplate.TemplateName)
      return "Template " + selectedTemplate.ID;
    return selectedTemplate.TemplateName;
  }

  return (
    <div className={styles.container}>
      <Popover.Root open={isOpen} onOpenChange={(open) => !isTemplateLoading && setIsOpen(open)}>
        <Popover.Trigger
          className={`${styles.trigger} ${isTemplateLoading ? styles.loading : ""} ${selectedTemplate?.DocxPath ? styles.docxTemplate : ""}`}
          aria-label="Select a template"
          disabled={isTemplateLoading}
        >
          {isTemplateLoading ? (
            <>
              {selectedTemplate?.TemplateName ? selectedTemplate.TemplateName : "Loading template..."}
              {/* <Loader2 size={16} className={styles.spinningIcon} /> */}{" "}
              {/* Arata mai bine fara ca e deja unul mai jos */}
            </>
          ) : (
            <>
              {getUITemplateName()}
              {selectedTemplate?.DocxPath && " 📄"}
              <div className={styles.dropdownArrow}>
                <ChevronsUpDown size={16} />
              </div>
            </>
          )}
        </Popover.Trigger>
        <Popover.Content className={styles.content} sideOffset={5}>
          <div className={styles.popoverContent}>{renderTemplateList}</div>
        </Popover.Content>
      </Popover.Root>
      {hasUnsavedChanges && (
        <button
          className={styles.saveButton}
          onClick={onSave}
          aria-label="Save current template changes"
          disabled={isLoading || isTemplateLoading}
        >
          {isLoading ? <Loader2 size={16} className={styles.spinningIcon} /> : "Save"}
        </button>
      )}
    </div>
  );
}
